import {
    ChevronsDownUp,
    ChevronsUpDown,
    Columns3,
    Download,
    FilterX,
    Search,
    X,
} from "lucide-react";
import React from "react";
import { PortfolioTableFeatures } from "./configs/types";

/**
 * Available grouping options for the portfolio table.
 */
export type GroupByOption = "none" | "currency" | "sector" | "group" | "secType" | "timezone";

export interface TableControlsProps {
  /** Current search query */
  searchQuery: string;
  /** Callback when search changes */
  onSearchChange: (query: string) => void;
  /** Callback to trigger CSV export */
  onExport: () => void;
  /** Current grouping mode */
  groupBy: GroupByOption;
  /** Callback when grouping changes */
  onGroupByChange: (value: GroupByOption) => void;
  /** Whether expanded columns are active */
  expandedColumnsActive: boolean;
  /** Callback to toggle expanded columns */
  onToggleExpandedColumns: () => void;
  /** Callback to expand/collapse all groups */
  onExpandAllGroups?: () => void;
  /** Whether all groups are currently expanded */
  allGroupsExpanded?: boolean;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether there is data to display */
  hasData: boolean;
  isDarkMode: boolean;
  /** Feature flags from config */
  features: PortfolioTableFeatures;
  /** Whether timezone grouping is available (tradableTimes data fetched) */
  hasTimezoneData?: boolean;
  /** Whether there are any active user-applied filters */
  hasActiveFilters?: boolean;
  /** Callback to clear all user-applied filters */
  onClearAllFilters?: () => void;
  /** Whether the controls are visible on mobile (controlled by parent) */
  mobileVisible?: boolean;
}

const TableControls: React.FC<TableControlsProps> = ({
  searchQuery,
  onSearchChange,
  onExport,
  groupBy,
  onGroupByChange,
  expandedColumnsActive,
  onToggleExpandedColumns,
  onExpandAllGroups,
  allGroupsExpanded,
  isLoading,
  hasData,
  isDarkMode,
  features,
  hasTimezoneData = false,
  hasActiveFilters = false,
  onClearAllFilters,
  mobileVisible = false,
}) => {
  return (
    <div className={`mb-2 mx-1 sm:mx-3 ${mobileVisible ? "block" : "hidden sm:block"}`}>
      {/* Controls container - always visible on desktop, controlled on mobile */}
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Search Bar */}
        {features.search && (
          <div className="relative flex-1">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? "text-teal-400" : "text-teal-600"
              }`}
            />
            <input
              type="text"
              placeholder="Search descriptions, types, sectors, groups, ConId..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`w-full pl-10 pr-10 py-2 text-sm rounded border-2 ${
                isDarkMode
                  ? "bg-gray-800 border-teal-600 text-white placeholder-gray-500 focus:border-teal-400"
                  : "bg-white border-teal-500 text-gray-900 placeholder-gray-400 focus:border-teal-600"
              } focus:outline-none focus:ring-2 focus:ring-teal-500/30`}
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange("")}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                  isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          {/* Clear Filters Button */}
          {hasActiveFilters && onClearAllFilters && (
            <button
              onClick={onClearAllFilters}
              className={`flex items-center justify-center gap-2 px-2 sm:px-3 py-2 text-sm rounded border transition-colors ${
                isDarkMode
                  ? "bg-red-900/30 border-red-600 text-red-300 hover:bg-red-900/50"
                  : "bg-red-50 border-red-400 text-red-600 hover:bg-red-100"
              }`}
              title="Clear all filters"
            >
              <FilterX className="w-4 h-4" />
              <span className="hidden sm:inline">Clear Filters</span>
            </button>
          )}

          {/* Export Button */}
          {features.export && (
            <button
              onClick={onExport}
              disabled={isLoading || !hasData}
              className={`flex items-center justify-center gap-2 px-2 sm:px-3 py-2 text-sm rounded border transition-colors ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600 text-gray-300 hover:border-teal-500 hover:text-teal-300 disabled:opacity-50 disabled:hover:border-gray-600 disabled:hover:text-gray-300"
                  : "bg-white border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-600 disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:text-gray-700"
              }`}
              title="Export to CSV"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          )}

          {/* Expand Columns Toggle */}
          <button
            onClick={onToggleExpandedColumns}
            className={`flex items-center justify-center gap-2 px-2 sm:px-3 py-2 text-sm rounded border transition-colors ${
              expandedColumnsActive
                ? isDarkMode
                  ? "bg-teal-900/50 border-teal-600 text-teal-300"
                  : "bg-teal-50 border-teal-500 text-teal-700"
                : isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500"
                : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
            }`}
            title={expandedColumnsActive ? "Show fewer columns" : "Show more columns"}
          >
            <Columns3 className="w-4 h-4" />
            <span>{expandedColumnsActive ? "- Columns" : "+ Columns"}</span>
          </button>

          {/* Grouping Dropdown */}
          {features.grouping && (
            <select
              value={groupBy}
              onChange={(e) => onGroupByChange(e.target.value as GroupByOption)}
              className={`px-2 sm:px-3 py-2 text-sm rounded border transition-colors cursor-pointer ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600 text-gray-300"
                  : "bg-white border-gray-300 text-gray-700"
              }`}
              title="Group positions"
            >
              <option value="none">Grouping: None</option>
              <option value="currency">Grouping: Currency</option>
              <option value="sector">Grouping: Sector</option>
              <option value="group">Grouping: Group</option>
              <option value="secType">Grouping: Type</option>
              {hasTimezoneData && <option value="timezone">Grouping: Timezone</option>}
            </select>
          )}

          {/* Expand/Collapse All Groups Button */}
          {features.grouping && groupBy !== "none" && onExpandAllGroups && (
            <button
              onClick={onExpandAllGroups}
              className={`flex items-center justify-center gap-2 px-2 sm:px-3 py-2 text-sm rounded border transition-colors ${
                isDarkMode
                  ? "bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500"
                  : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
              title={allGroupsExpanded ? "Collapse all groups" : "Expand all groups"}
            >
              {allGroupsExpanded ? (
                <ChevronsDownUp className="w-4 h-4" />
              ) : (
                <ChevronsUpDown className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{allGroupsExpanded ? "Collapse" : "Expand"}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableControls;

